// src/features/agent-builder/components/CustomToolItem.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Badge, Tooltip, ToggleSwitchField } from '@dopomogai/ui'; // Added ToggleSwitchField
import { Settings, Trash2, Construction, Lock } from 'lucide-react';
import clsx from 'clsx';
import type { CustomToolData } from '@dopomogai/types'; // Make sure this type is updated
import { AITestGeneratorButtonTeaser } from '../teasers/AITestGeneratorButtonTeaser';
import { renderIcon } from '@dopomogai/utils';

interface CustomToolItemProps {
  tool: CustomToolData; // Ensure this includes isEnabled and isMutable
  onToggle: (toolId: string, isEnabled: boolean) => void; // Added onToggle
  onEdit: (toolId: string) => void;
  onDelete: (toolId: string) => void;
}

export const CustomToolItem: React.FC<CustomToolItemProps> = ({ tool, onToggle, onEdit, onDelete }) => {
  const { t } = useTranslation(['agentBuilder', 'common']);

  const customBadgeTextKey = "agentBuilder:step3.customSkills.customBadge";
  const isActuallyMutable = tool.isMutable !== false; // Custom tools default to mutable for their enabled state

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isActuallyMutable) {
      onToggle(tool.id, event.target.checked);
    }
  };

  const toggleLabelText = isActuallyMutable
    ? (tool.isEnabled ? t('common:enabled') : t('common:disabled'))
    : t('common:locked');


  const editTooltipText = t('common:editTooltip', { itemName: tool.name });
  const editAriaLabelText = t('common:editAriaLabel', { itemName: tool.name });
  const deleteTooltipText = t('common:deleteTooltip', { itemName: tool.name });
  const deleteAriaLabelText = t('common:deleteAriaLabel', { itemName: tool.name });

  return (
    <div className={clsx(
      "flex items-center justify-between p-4 bg-white rounded-xl",
      "border-2 border-black",
      "transition-colors duration-150 ease-in-out",
      isActuallyMutable && "hover:bg-slate-50", // Hover effect only if mutable elements are present
      !isActuallyMutable && !tool.isEnabled && "opacity-70" // If locked and disabled, slightly dim
    )}>
      {/* Left Section: Icon and Info */}
      <div className="flex items-start gap-4 flex-grow min-w-0 pr-4">
        <div className="flex-shrink-0 pt-1 text-slate-600">
          {renderIcon(tool.icon || <Construction size={24} />, { size: 28, className: "opacity-80" })}
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-md font-semibold text-slate-800 truncate" title={tool.name}>{tool.name}</h3>
            <Badge
              textKey={customBadgeTextKey}
              accentColor="blue"
              className="text-xs"
            />
          </div>
          {tool.description && (
            <p className="text-sm text-slate-600 leading-relaxed line-clamp-2" title={tool.description}>{tool.description}</p>
          )}
        </div>
      </div>

      {/* Right Section: Actions & Toggle */}
      <div className="ml-auto flex items-center gap-3 flex-shrink-0 pl-2"> {/* Increased gap for more actions */}
        <AITestGeneratorButtonTeaser />
        <Tooltip text={editTooltipText}>
          <IconButton
            icon={<Settings size={18} />} // Slightly larger icon
            ariaLabel={editAriaLabelText}
            onClick={() => onEdit(tool.id)}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-indigo-600"
          />
        </Tooltip>
        <Tooltip text={deleteTooltipText}>
          <IconButton
            icon={<Trash2 size={18} />} // Slightly larger icon
            ariaLabel={deleteAriaLabelText}
            onClick={() => onDelete(tool.id)}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-600"
          />
        </Tooltip>
        {!isActuallyMutable && tool.isEnabled && ( // Show lock icon if it's locked ON
          <Tooltip text={t('common:lockedTooltip', 'This tool is locked and cannot be disabled.')}>
            <Lock size={18} className="text-slate-500" />
          </Tooltip>
        )}
        <ToggleSwitchField
          id={`custom-tool-toggle-${tool.id}`}
          checked={!!tool.isEnabled}
          onChange={handleToggleChange}
          disabled={!isActuallyMutable}
          aria-label={isActuallyMutable ? `${tool.name}: ${toggleLabelText}` : `${tool.name}: ${t('common:locked')}`}
          // Assuming ToggleSwitchField from @dopomogai/ui is styled correctly
        />
      </div>
    </div>
  );
};